import { yupResolver } from "@hookform/resolvers/yup";
import { HStack, Select, View } from "native-base";
import React from "react";
import { useForm } from "react-hook-form";
import { TextInputMask } from "react-native-masked-text";

import FormButton from "@/components/atoms/FormButton";
import SecureTextInput from "@/components/atoms/SecureTextInput";
import TermsButton from "@/components/atoms/TermsButton";
import TextInput from "@/components/atoms/TextInput";
import FormField from "@/components/molecules/FormField";
import { CompanyType } from "@/interfaces/domain/CompanyType";

import { SignUpFormComponentProps } from "../../types";
import { CompanyFormData, schema } from "../schema";

type FormProps = {
  companyTypes: CompanyType[];
} & SignUpFormComponentProps<CompanyFormData>;

const Form = ({ companyTypes, onCancel, onSubmit }: FormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: yupResolver(schema),
  });

  return (
    <View w="full">
      <FormField
        control={control}
        name="tipoNegocio"
        label="Tipo de negócio"
        required
        error={errors.tipoNegocio}
        render={(value, onChange) => (
          <Select
            variant="underlined"
            accessibilityLabel="Escolha um tipo"
            placeholder="Escolha um tipo"
            fontSize="md"
            selectedValue={value}
            onValueChange={(itemValue: string) => {
              onChange(itemValue);
            }}>
            {companyTypes?.map((type) => (
              <Select.Item key={type._id} label={type.nome} value={type._id} />
            ))}
          </Select>
        )}
      />
      <FormField
        control={control}
        name="cnpj"
        label="CNPJ"
        required
        error={errors.cnpj}
        render={(value, onChange) => (
          <TextInputMask
            value={value}
            onChangeText={onChange}
            customTextInput={TextInput}
            type={"cnpj"}
            placeholder="CNPJ"
          />
        )}
      />
      <FormField
        control={control}
        name="nome"
        label="Nome fantasia"
        required
        error={errors.nome}
        render={(value, onChange) => (
          <TextInput value={value} onChangeText={onChange} placeholder="Nome fantasia" />
        )}
      />
      <FormField
        control={control}
        name="razaoSocial"
        label="Razão social"
        required
        error={errors.razaoSocial}
        render={(value, onChange) => (
          <TextInput value={value} onChangeText={onChange} placeholder="Razão social" />
        )}
      />
      <HStack w="1/2" space={2}>
        <FormField
          control={control}
          name="telefone"
          label="Telefone"
          required
          error={errors.telefone}
          render={(value, onChange) => (
            <TextInputMask
              value={value}
              onChangeText={onChange}
              customTextInput={TextInput}
              type={"cel-phone"}
              options={{
                maskType: "BRL",
                withDDD: true,
                dddMask: "(99) ",
              }}
              placeholder="Telefone"
            />
          )}
        />
        <FormField
          control={control}
          name="mediaFaturamento"
          label="Faturamento mensal"
          error={errors.mediaFaturamento}
          render={(value, onChange) => (
            <TextInputMask
              value={value}
              onChangeText={onChange}
              customTextInput={TextInput}
              includeRawValueInChangeText={true}
              type={"money"}
              options={{
                precision: 2,
                separator: ",",
                delimiter: ".",
                unit: "R$ ",
                suffixUnit: "",
              }}
              placeholder="Média de faturamento"
            />
          )}
        />
      </HStack>
      <FormField
        control={control}
        name="email"
        label="E-mail"
        required
        error={errors.email}
        render={(value, onChange) => (
          <TextInput value={value} onChangeText={onChange} placeholder="E-mail" />
        )}
      />
      <FormField
        control={control}
        name="emailConfirm"
        label="Confirme o E-mail"
        required
        error={errors.emailConfirm}
        render={(value, onChange) => (
          <TextInput
            value={value}
            keyboardType="email-address"
            autoCapitalize="none"
            onChangeText={onChange}
            placeholder="Confirme o E-mail"
          />
        )}
      />
      <FormField
        control={control}
        name="senha"
        label="Senha"
        hint="Pelo menos 8 caracteres com letras e números"
        required
        error={errors.senha}
        render={(value, onChange) => (
          <SecureTextInput
            value={value}
            autoCapitalize="none"
            onChangeText={onChange}
            placeholder="Senha"
          />
        )}
      />
      <FormField
        control={control}
        name="senhaConfirm"
        label="Confirma e senha"
        hint="Pelo menos 8 caracteres com letras e números"
        required
        error={errors.senhaConfirm}
        render={(value, onChange) => (
          <SecureTextInput
            value={value}
            autoCapitalize="none"
            onChangeText={onChange}
            placeholder="Confirma e senha"
          />
        )}
      />
      <FormButton mt="10" onPress={handleSubmit(onSubmit)}>
        Cadastrar
      </FormButton>
      <FormButton
        colorScheme="white"
        textColor="primary.700"
        my="4"
        shadow="2"
        onPress={() => onCancel()}>
        Cancelar
      </FormButton>
      <TermsButton />
    </View>
  );
};

export default Form;
